import { useCallback, useEffect, useState } from 'react'
import { useI18n, formatar } from '../lib/i18n'

/**
 * Créditos e loja de equipamento.
 *
 * Duas regras de docs/07-gamificacao.md moldam a tela:
 *
 * §2 — nada que afete desempenho de RF se compra com dinheiro real. Não existe
 * botão de pagar aqui, e nunca vai existir: o aviso fica visível. Crédito só
 * se ganha operando.
 *
 * §5 — o extrato mostra DE ONDE veio cada crédito. Um saldo sem explicação é
 * número arbitrário, e número arbitrário não ensina nada sobre o hobby. Vendo
 * "País novo · 7 × 60", o operador entende que explorar paga.
 *
 * A sequência de semanas ativas (§5: "streak sem punição") também mora aqui,
 * junto do saldo — é outra fonte de crédito, e o extrato abaixo já explica
 * ela sozinho quando aparece a linha "Sequência de semanas ativas".
 */

interface Line {
  label: string
  count: number
  each?: number
  credits: number
}

interface Statement {
  earned: number
  spent: number
  balance: number
  lines: Line[]
  spending: Line[]
  // Semanas seguidas com pelo menos um QSO válido. streak_weeks é a sequência
  // ATUAL (cai a zero se a pessoa some por mais de uma semana — só decoração,
  // não afeta saldo). streak_record é a maior já alcançada: é ela que paga
  // crédito, porque só cresce — nunca corrige o saldo já gasto pra baixo.
  streak_weeks: number
  streak_record: number
  streak_next_goal?: number
}

interface Item {
  id: string
  kind: string
  name: string
  price: number
  detail: string
  owned: boolean
  affordable: boolean
}

interface EconomyResponse {
  statement: Statement
  shop: Item[] | null
}

export function Shack({ refreshKey }: { refreshKey?: number }) {
  const { t } = useI18n()
  const KIND_NAME: Record<string, string> = {
    rig: t.shack.rig,
    antenna: t.shack.antenna,
    feedline: t.shack.feedline,
    amplifier: t.shack.amplifier,
    rotator: t.shack.rotator,
    tower: t.shack.tower,
    filter: t.shack.filter,
  }
  const [st, setSt] = useState<Statement | null>(null)
  const [shop, setShop] = useState<Item[]>([])
  const [open, setOpen] = useState(false)
  const [showLedger, setShowLedger] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(() => {
    fetch('/api/economy')
      .then((r) => r.json())
      .then((d: EconomyResponse) => {
        setSt(d.statement)
        setShop(d.shop ?? [])
      })
      .catch(() => {})
  }, [])

  useEffect(load, [load, refreshKey])

  const buy = async (item: Item) => {
    setBusy(item.id)
    setMsg(null)
    try {
      const r = await fetch('/api/economy/buy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: item.id }),
      })
      const d = (await r.json()) as EconomyResponse & { message?: string }
      if (!r.ok) {
        setMsg(d.message ?? t.shack.erroComprar)
        return
      }
      setSt(d.statement)
      setShop(d.shop ?? [])
      setMsg(`${item.name} ${t.shack.eSeu}`)
    } catch {
      setMsg(t.shack.erroComprar)
    } finally {
      setBusy(null)
    }
  }

  const kinds = ['rig', 'antenna', 'feedline', 'amplifier', 'rotator', 'tower', 'filter']

  return (
    <section className="shack">
      <button className="shack-toggle" onClick={() => setOpen((v) => !v)}>
        {t.shack.titulo}
        {st && (
          <small>
            {st.balance} {t.shack.creditos}
            {st.streak_weeks > 0 && ` · ${st.streak_weeks} ${t.shack.semSeguidas}`}
          </small>
        )}
      </button>

      {open && st && (
        <>
          <div className="shack-balance">
            <b>{st.balance}</b>
            <span>{t.shack.creditosDisponiveis}</span>
            <button className="link" onClick={() => setShowLedger((v) => !v)}>
              {showLedger ? t.shack.ocultarExtrato : t.shack.deOndeVeio}
            </button>
          </div>

          {(st.streak_weeks > 0 || st.streak_record > 0) && (
            <p className="streak-info">
              {st.streak_weeks > 0
                ? formatar(
                    st.streak_weeks === 1 ? t.shack.sequenciaAtualSingular : t.shack.sequenciaAtualPlural,
                    { n: st.streak_weeks },
                  )
                : formatar(
                    st.streak_record === 1 ? t.shack.semSequenciaSingular : t.shack.semSequenciaPlural,
                    { n: st.streak_record },
                  )}
              {st.streak_weeks > 0 && st.streak_next_goal
                ? ` · ${formatar(t.shack.faltamParaBonus, { n: st.streak_next_goal - st.streak_weeks })}`
                : ''}
            </p>
          )}

          {showLedger && (
            <ul className="ledger">
              {st.lines.map((l, i) => (
                <li key={i}>
                  <span>{l.label}</span>
                  <span className="times">
                    {l.each ? `${l.count} × ${l.each}` : `${l.count}`}
                  </span>
                  <b>+{l.credits}</b>
                </li>
              ))}
              {st.spending.map((l, i) => (
                <li key={`s${i}`} className="spent">
                  <span>{l.label}</span>
                  <span className="times" />
                  <b>−{l.credits}</b>
                </li>
              ))}
              {st.lines.length === 0 && (
                <li className="empty">
                  <span>{t.shack.ledgerVazio}</span>
                </li>
              )}
            </ul>
          )}

          {msg && <p className="shack-msg">{msg}</p>}

          {kinds.map((kind) => (
            <div key={kind} className="shop-group">
              <h3>{KIND_NAME[kind]}</h3>
              <ul className="shop">
                {shop
                  .filter((it) => it.kind === kind)
                  .map((it) => (
                    <li key={it.id} className={it.owned ? 'item owned' : 'item'}>
                      <div className="item-head">
                        <strong>{it.name}</strong>
                        <span className="grow" />
                        {it.owned ? (
                          <span className="pill on">{t.shack.seu}</span>
                        ) : (
                          <button
                            disabled={!it.affordable || busy === it.id}
                            onClick={() => void buy(it)}
                          >
                            {it.price} {t.shack.cr}
                          </button>
                        )}
                      </div>
                      <p className="item-detail">{it.detail}</p>
                    </li>
                  ))}
              </ul>
            </div>
          ))}

          <p className="shack-note">{t.shack.nota}</p>
        </>
      )}
    </section>
  )
}
